import React, { useEffect, useState } from 'react';
import { ReactTranscript } from './ReactTranscript';
import {
    getInputFulfillment,
    InputFulfillmentResponse,
} from './api/getInputFulfillment';
import {
    FaqData,
    getFaqDataForIntent,
    shouldPresentSendMessageFallbackForIntent,
    MessageAssistQnaData,
    ProvidenceButtonListButton,
    ProvidenceButtonListData,
} from './attachments';
import { hooks } from 'botframework-webchat-component';
import { ApplicationModalPropsType, ButtonData, InputBox } from './jsxElements';
import { isUserWaitingEvent } from './utils/conversationStatus';
import { Activity, AttachmentContent, TenantGreetingConfig } from './types';
import { useConversationStatus } from './contextProviders/ConversationStatusContext';
import { exportTranscript } from './botium';
import {
    getUrlParam,
    isGenesysResume,
    isRehydratedMessage,
    isLatestRehydratedMessage,
    LiveChatEndConfirmation,
    setupMessageListener,
    closeChatBot,
    setEntrypointUtterance,
    getGreetingConfigMessages,
    getTenantName,
    isMobileBrowser,
    jsonObjectIsEmpty,
    postChatActivity,
    getGreetingConfigMainMenu,
} from './helpers';
import {
    OnLiveChatClosedEvent,
    OnLiveChatClosedInTabEvent,
    OnLiveChatOpeningEvent,
    getLiveChatService,
} from './liveChatService';
import { logEvent as logAnalyticsEvent } from './analytics';
import { logEvent as logInsightsEvent } from './logging';
import {
    craftMAFallbackErrorMessage,
    craftMAFallbackMessage,
    craftMAFaqMessage,
    craftMANotProviderMessage,
    craftMANotProviderMessageWithFulfillment,
    postMessageAssistMessage,
} from './messageassist';
import styles from './ReactChat.module.css';
import quickMessageConfig from './resources/quickMessageConfig.json';
import {
    craftCustomActivityMessage,
    getLastBotMessage,
    getLastBotActivity,
    hasMultipleUserMessages,
} from './messageUtils';
import { WebChatActivity } from 'botframework-webchat-core';
import {
    DisableReasons,
    InputMode,
    useConfig,
    useDisableTextSend,
} from './hooks';
import { AmpFlagSet } from './amplitude';
import { MessageRenderer } from './interfaces/messageRenderer';
import {
    CHARACTER_LIMIT_EVENT_NAME,
    CHARACTER_LIMIT_RESET_EVENT_NAME,
    EVENT_RESTART_CHATBOT,
    INACTIVE_TIMEOUT_MS,
    MESSAGE_ASSIST_FULFILLMENT_TYPES,
    MESSAGE_ASSIST_MESSAGE_EVENT_TYPES,
    MESSAGE_ASSIST_SCHEMA,
    TENANTS,
} from './constants';
import { NLU_FALLBACK_INTENT } from './intents';
import { GraceFeatureFlags } from './featureFlags';
import isFulfillmentAvailable from './utils/isFulfillmentAvailable';
import getNumberFromAny from './utils/getNumberValFromAny';
import { useInformationalAlert } from './contextProviders';
import { presentMASendMessage } from './messageassist/presentMASendMessage';

let lastActive: number;

const { useActivities, useSendEvent, useSendMessage, useSendPostBack } = hooks;

const liveChatService = getLiveChatService();

const isDisplayable = (activity: Activity) => {
    return (
        activity.type === 'message' ||
        (activity.type === 'event' && activity.name === 'feedbackEvent') ||
        (activity.type === 'event' && activity.name === 'liveChatMessage') ||
        (activity.type === 'event' && activity.name === 'userWaiting')
    );
};

const isRestartEvent = (activity: Activity): boolean => {
    return activity.type === 'event' && activity.name === EVENT_RESTART_CHATBOT;
};

function getMessages(activities: WebChatActivity[]) {
    const INTERRUPTING_SCENARIO_TEXT = 'Now back to the previous topic…';
    const LIVE_CHAT_HEARTBEAT = '/DIRECT-live-chat-heartbeat';

    // filter out the Healthbot's two default bot messages when returning from an interrupting scenario
    // the first is "Now back to the previous topic…"
    // the second is the last bot message before the conversation was interrupted
    let messages = (activities as Activity[]).filter(
        (activity: Activity, idx: number, msgs: Activity[]) => {
            const prevMessage = msgs[idx - 1];
            const secondPrevMessage = msgs[idx - 2];
            // Close Grace in all other tabs when a chat is restarted
            if (isRestartEvent(activity)) {
                closeChatBot(false);
            }
            if (
                (isUserWaitingEvent(prevMessage) ||
                    isUserWaitingEvent(secondPrevMessage)) &&
                isUserWaitingEvent(activity)
            ) {
                return false;
            }
            return (
                isDisplayable(activity) &&
                activity.text !== INTERRUPTING_SCENARIO_TEXT &&
                activity.text !== LIVE_CHAT_HEARTBEAT &&
                !activity?.text?.startsWith(
                    '/navigation.resume-conversation'
                ) &&
                (!prevMessage ||
                    prevMessage.text !== INTERRUPTING_SCENARIO_TEXT)
            );
        }
    );

    let entrypointUtterance = getUrlParam('entrypointUtterance');
    if (entrypointUtterance) {
        if (entrypointUtterance.includes('+')) {
            entrypointUtterance = entrypointUtterance.replaceAll('+', ' ');
        }
        const entrypointMessage = {
            text: entrypointUtterance,
            from: {
                id: '',
                name: 'you',
                role: 'user',
            },
            id: 'entrypointUtterance',
            type: 'message',
        } as Activity;
        messages = [entrypointMessage, ...messages];
    }
    return messages;
}

const postMessageAssistFulfillmentShownMessage = async (
    fulfillment: string,
    fulfillmentType: string
) => {
    // Make sure no spaces in string
    fulfillment = fulfillment.replaceAll(' ', '');
    fulfillmentType = fulfillmentType.replaceAll(' ', '');

    await postMessageAssistMessage(
        `${MESSAGE_ASSIST_SCHEMA}${MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.FULFILLMENT_SHOWN}`,
        undefined,
        `{"fulfillment": "${fulfillment}", "fulfillmentType": "${fulfillmentType}"}`
    );
};

export const setFulfillmentLinkShownForGreetingOptimization = async (
    fulfillment: string | undefined
) => {
    if (!fulfillment) {
        return;
    }
    await postMessageAssistFulfillmentShownMessage(fulfillment, 'qna');
};

export const setFulfillmentLinkShown = async (
    contentType: string,
    content: AttachmentContent
) => {
    let fulfillment = 'none';
    let fulfillmentType = 'none';
    // If type buttonlist, this could be a flow like med management
    if (contentType === 'application/providence.buttonlist') {
        const buttonContent = content as ProvidenceButtonListData;
        // Find the flow or intent, preferring flow
        const buttonWithFlow = buttonContent.buttons.find(
            (button: ProvidenceButtonListButton) => button.flow || button.intent
        );
        if (buttonWithFlow) {
            // Prefer flow
            fulfillment =
                buttonWithFlow.flow ?? buttonWithFlow.intent ?? 'none';
            fulfillmentType = MESSAGE_ASSIST_FULFILLMENT_TYPES.FLOW;
        } else {
            return;
        }
    }
    // If type messageAssistQnaResponse, this is always from an intent
    else if (
        contentType === 'application/providence.messageAssistQnaResponse'
    ) {
        const qnaData = content as MessageAssistQnaData;
        // Find the intent
        // If intent is present in qnaData, use it
        // If not, find the first button with an intent
        if (qnaData?.qnaIntent) {
            fulfillment = qnaData.qnaIntent;
        } else {
            const buttonWithIntent = qnaData.actions?.find(
                (button: ButtonData) => button.intent
            );
            if (buttonWithIntent) {
                fulfillment = buttonWithIntent.intent ?? 'none';
            } else {
                return;
            }
        }
        fulfillmentType = MESSAGE_ASSIST_FULFILLMENT_TYPES.QNA;
    }

    await postMessageAssistFulfillmentShownMessage(
        fulfillment,
        fulfillmentType
    );
};

export const APIBasedReactChat: React.FC<{
    featureFlags: AmpFlagSet;
    healthSystemToken: string | null;
    modalProperties?: ApplicationModalPropsType;
    tenantGreetingConfig?: TenantGreetingConfig;
    setModalProperties:
        | ((modalProperties: ApplicationModalPropsType) => void)
        | undefined;
    setInputMode: (inputMode: InputMode) => void;
    setGreetingMessages: (greetingMessages: any[]) => void;
    setShouldInitDirectLine: (shouldInit: boolean) => void;
    messageRenderer: MessageRenderer;
}> = ({
    featureFlags,
    healthSystemToken,
    modalProperties,
    tenantGreetingConfig,
    setModalProperties,
    setInputMode,
    setGreetingMessages,
    setShouldInitDirectLine,
    messageRenderer,
}) => {
    const { authServiceEndpoint } = useConfig();
    const [messages, setMessages] = useState<any[]>([]);
    const { inactive, setInactive } = useConversationStatus();
    const [activeIntervalId, setActiveIntervalId] = useState<
        NodeJS.Timeout | undefined
    >();
    const [interactedWithBot, setInteractedWithBot] = useState(false);
    const lastBotActivity = getLastBotActivity(messages);
    const { setDisableTextSend } = useDisableTextSend();
    const { showGlobalInformationAlert } = useInformationalAlert();

    const [
        fulfilmentResponseAfterUserThinksBotIsProvider,
        setFulfilmentResponseAfterUserThinksBotIsProvider,
    ] = useState<InputFulfillmentResponse | undefined>(undefined);

    const startActivityTimer = () => {
        resetLastActiveTime();
        setActiveIntervalId(
            setInterval(() => {
                const now = new Date().getTime();
                if (lastActive + INACTIVE_TIMEOUT_MS < now) {
                    setInputMode('disabled');
                    setInactive(true);
                    stopActivityTimer();
                }
            }, 10000)
        );
    };

    const stopActivityTimer = () => {
        if (activeIntervalId !== undefined) {
            clearInterval(activeIntervalId);
            setActiveIntervalId(undefined);
        }
    };

    const resetLastActiveTime = () => {
        lastActive = new Date().getTime();
    };

    const presentMAFaqResponse = (
        messages: any[],
        fulfillment: any,
        utterance: string,
        intentFaqData?: FaqData
    ) => {
        const { intent, answer, source } = fulfillment;

        logInsightsEvent({
            name: 'No conversation required for user input',
        });

        logAnalyticsEvent('User Utterance Understood - FAQ Answered', {
            kb: intent,
        });
        setMessages([
            ...messages,
            craftMAFaqMessage(answer, intent, source, utterance, intentFaqData),
        ]);
    };
    const presentMANotProvider = (
        messages: any[],
        utterance: string,
        intent: string,
        source: string
    ) => {
        logAnalyticsEvent('I am a bot - Shown');
        setInputMode('disabled');
        setMessages([
            ...messages,
            craftMANotProviderMessage(utterance, intent, source),
        ]);
    };

    const presentMANotProviderWithFulfillment = (
        messages: any[],
        utterance: string,
        intent: string,
        source: string
    ) => {
        logAnalyticsEvent('I am a bot - Shown');
        setInputMode('disabled');
        setMessages([
            ...messages,
            craftMANotProviderMessageWithFulfillment(
                utterance,
                intent,
                source,
                featureFlags
            ),
        ]);
    };

    const presentMAErrorFallback = (messages: any[], utterance: string) => {
        logInsightsEvent({
            name: 'Error - No actionable fulfillment returned for user input',
        });
        logAnalyticsEvent('Fallback Response - Shown', {
            fallback_message_type: 'Fallback Response - Shown',
            intent: NLU_FALLBACK_INTENT,
        });
        setInputMode('disabled');
        setMessages([...messages, craftMAFallbackErrorMessage(utterance)]);
    };

    const presentMAFallback = (
        messages: any[],
        utterance: string,
        intent: string,
        source: string,
        faqData?: FaqData
    ) => {
        logInsightsEvent({
            name: 'No actionable fulfillment returned for user input',
        });
        logAnalyticsEvent('Fallback Response - Shown', {
            fallback_message_type: 'Fallback Response - Shown',
            intent: intent || NLU_FALLBACK_INTENT,
        });
        setInputMode('disabled');
        setMessages([
            ...messages,
            craftMAFallbackMessage(utterance, intent, source, faqData),
        ]);
    };

    const deferToDirectLine = (
        messages: any[],
        input: string,
        intent?: string
    ) => {
        logInsightsEvent({
            name: 'Conversation required for user input',
        });
        stopActivityTimer();
        // Store entrypoint to be passed to Rasa so
        // conversation can continue where it left off
        // Note: this can either be a text utterance or a button action
        setEntrypointUtterance(input);
        setGreetingMessages([...messages]);
        logAnalyticsEvent('Rasa conversation starts', { intent });
        setShouldInitDirectLine(true);
    };

    async function handleUserInput(
        text: string,
        messages: any[],
        isButton: boolean
    ): Promise<void> {
        setFulfilmentResponseAfterUserThinksBotIsProvider(undefined);

        const utterance = isButton ? '' : text;
        // Call fulfillment API to get FAQ answer and intent
        let fulfillment: InputFulfillmentResponse | undefined;

        try {
            setDisableTextSend(true, DisableReasons.GREETING_OPTIMIZATION);
            fulfillment = await getInputFulfillment(
                text,
                authServiceEndpoint,
                healthSystemToken,
                featureFlags
            );
        } catch (error) {
            presentMAErrorFallback(messages, utterance);
            setDisableTextSend(false, DisableReasons.GREETING_OPTIMIZATION);
            return;
        }

        const intent = fulfillment?.intent || '';
        const userThinksTheyAreTalkingToProvider =
            fulfillment?.user_thinks_they_are_talking_to_a_provider;

        const intentFaqData = getFaqDataForIntent(intent, featureFlags);
        const canFulfill = isFulfillmentAvailable(fulfillment, intentFaqData);

        if (userThinksTheyAreTalkingToProvider && !intentFaqData?.isEmergency) {
            logInsightsEvent({
                name: 'User thinks they are talking to a provider',
            });

            // present the MA not provider message if
            // the feature flag is enabled
            if (featureFlags[GraceFeatureFlags.MessageAssistIAmABot]) {
                logInsightsEvent({
                    name: 'Telling the user that they are talking to a bot',
                });

                if (canFulfill && fulfillment != undefined) {
                    setFulfilmentResponseAfterUserThinksBotIsProvider(
                        fulfillment
                    );

                    presentMANotProviderWithFulfillment(
                        messages,
                        utterance,
                        intent,
                        fulfillment.source
                    );
                } else {
                    presentMANotProvider(
                        messages,
                        utterance,
                        intent,
                        fulfillment?.source || ''
                    );
                }
                setDisableTextSend(false, DisableReasons.GREETING_OPTIMIZATION);
                return;
            }
        }

        // Check if there is available fulfillment
        if (canFulfill) {
            // Defer to DirectLine for full conversation flow if intent requires it and shouldn't be bypassed
            if (intentFaqData?.requiresConversation) {
                deferToDirectLine(messages, text, intent);
                postMessageAssistFulfillmentShownMessage(
                    intent,
                    MESSAGE_ASSIST_FULFILLMENT_TYPES.FLOW
                );
            } else {
                // Present FAQ response - either no conversation required or bypassing due to flag
                setFulfillmentLinkShownForGreetingOptimization(intent);
                presentMAFaqResponse(
                    messages,
                    fulfillment,
                    utterance,
                    intentFaqData
                );
            }
        } else if (shouldPresentSendMessageFallbackForIntent(intent)) {
            presentMASendMessage(
                utterance,
                intent,
                '',
                {
                    featureFlags,
                    setInputMode,
                    messages,
                    setMessages,
                    showGlobalInformationAlert,
                },
                intentFaqData
            );
        } else {
            presentMAFallback(messages, utterance, intent, '', intentFaqData);
        }

        // Re-enable text input after fulfillment processing is complete
        setDisableTextSend(false, DisableReasons.GREETING_OPTIMIZATION);
    }

    const appendMessage = (message: Activity) => {
        // Append bot message to the transcript
        const updatedMessages = [...messages, message];
        setMessages(updatedMessages);
    };
    const sendMessage = (text: string) => {
        resetLastActiveTime();
        setInteractedWithBot(true);

        if (fulfilmentResponseAfterUserThinksBotIsProvider == undefined) {
            // Append user's message to the transcript
            const updatedMessages = [
                ...messages,
                {
                    text,
                    type: 'message',
                    from: { role: 'user' },
                    timestamp: new Date().toISOString(),
                },
            ];
            setMessages(updatedMessages);
            handleUserInput(text, updatedMessages, false);
        } else {
            const fulfillment =
                fulfilmentResponseAfterUserThinksBotIsProvider as InputFulfillmentResponse;
            const intent = fulfillment.intent || '';
            const intentFaqData = getFaqDataForIntent(intent, featureFlags);

            if (intentFaqData?.requiresConversation) {
                deferToDirectLine(messages, text, intent);
            } else {
                // Present FAQ response - either no conversation required or bypassing due to flag
                setFulfillmentLinkShownForGreetingOptimization(intent);
                presentMAFaqResponse(
                    messages,
                    fulfillment,
                    text,
                    intentFaqData
                );
            }

            setFulfilmentResponseAfterUserThinksBotIsProvider(undefined);
        }
    };
    const sendPostBack = (message: any) => {
        resetLastActiveTime();
        // Append user's message to the transcript
        const text = message.displayText;
        const action = message.text;
        const updatedMessages = [
            ...messages,
            {
                text,
                type: 'message',
                from: { role: 'user' },
                timestamp: new Date().toISOString(),
            },
        ];
        if (message.returnToMenu) {
            const mainMenu = getGreetingConfigMainMenu(tenantGreetingConfig);
            if (mainMenu) {
                mainMenu.text =
                    tenantGreetingConfig?.mainMenu?.repromptText || '';
                updatedMessages.push(mainMenu);
                logAnalyticsEvent('Main Menu');
            }
            setInputMode('enabled');
            setMessages(updatedMessages);
        } else {
            setMessages(updatedMessages);
            handleUserInput(action || text, updatedMessages, true);
        }
    };

    useEffect(() => {
        if (jsonObjectIsEmpty(tenantGreetingConfig)) {
            return;
        }
        const tenantGreetingMessages =
            getGreetingConfigMessages(tenantGreetingConfig);
        if (!tenantGreetingMessages) {
            logInsightsEvent({
                name: 'Unable to pull messages from greeting config',
            });
            setShouldInitDirectLine(true);
            return;
        }
        const tenantGreetingMainMenu =
            getGreetingConfigMainMenu(tenantGreetingConfig);
        if (!tenantGreetingMainMenu) {
            logInsightsEvent({
                name: 'Unable to pull main menu from greeting config',
            });
        } else {
            tenantGreetingMessages.push(tenantGreetingMainMenu);
        }
        setMessages(tenantGreetingMessages);
        startActivityTimer();
        logInsightsEvent({ name: 'Displayed greeting from config' });
        logAnalyticsEvent('Greeting Message', {
            browser: isMobileBrowser() ? 'Mobile-Web' : 'Desktop',
            chatbot_tenant: `grace-${getTenantName()}`,
            open_menu_selection:
                getUrlParam('entrypointUtterance')?.replaceAll('+', ' ') ||
                'Grace CTA',
            site_referral: getUrlParam('referrer').split('?')[0],
            viewport: window.screen.width + ' x ' + window.screen.height,
        });
        tenantGreetingMessages.forEach((message) => {
            if (message.isMainMenu) {
                logAnalyticsEvent('Main Menu');
            }
        });
        // Display loaded chat with greeting and text input to user
        postChatActivity();
        setInputMode('enabled');
    }, [tenantGreetingConfig]);

    useEffect(() => {
        if (inactive) {
            const inactiveMessage: Activity = craftCustomActivityMessage(
                quickMessageConfig.inactive
            );
            setMessages([inactiveMessage]);
        }
    }, [inactive]);

    // 420 = default rasa character limit
    // https://rasa.com/docs/reference/config/components/llm-command-generators/#customizing-the-maximum-length-of-user-input
    const inputCharacterCountLimit = getNumberFromAny(
        featureFlags[GraceFeatureFlags.ChatbotConfigInputCharacterCountLimit],
        420
    );
    const inputRemainingCharsWarningThreshold = getNumberFromAny(
        featureFlags[GraceFeatureFlags.ChatbotConfigInputCharWarningThreshold],
        50
    );

    const { setInteractionAnchors } = useConversationStatus();

    // Sync provider with the last bot activity and message
    useEffect(() => {
        setInteractionAnchors({
            lastMessage: messages[messages.length - 1] as Activity,
            lastBotActivity,
        });
    }, [messages, lastBotActivity, setInteractionAnchors]);

    return (
        <div
            className={styles.reactChatContainer}
            onFocus={() => setInteractedWithBot(true)}
        >
            <ReactTranscript
                messages={messages}
                appendMessage={appendMessage}
                sendMessage={sendMessage}
                sendPostBack={sendPostBack}
                interactedWithBot={interactedWithBot}
                sendEvent={() => {
                    /*Not implemented*/
                }}
                featureFlags={featureFlags}
                modalProperties={modalProperties}
                setInputMode={setInputMode}
                setModalProperties={setModalProperties}
                liveChatService={undefined}
                messageRenderer={messageRenderer}
            />
            <InputBox
                sendMessage={sendMessage}
                focusCounter={messages.length}
                isMenu={true}
                shouldLogAnalyticsEvents={true}
                inputCharacterCountLimit={inputCharacterCountLimit}
                inputRemainingCharsWarningThreshold={
                    inputRemainingCharsWarningThreshold
                }
            />
        </div>
    );
};

const ReactChat: React.FC<{
    featureFlags: AmpFlagSet;
    modalProperties: ApplicationModalPropsType | undefined;
    setModalProperties:
        | ((modalProperties: ApplicationModalPropsType) => void)
        | undefined;
    interactedWithBot: boolean;
    setFeedbackDialogIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setInputMode: (inputMode: InputMode) => void;
    setInteractedWithBot: (interacted: boolean) => void;
    setLiveChatEndConfirmation: React.Dispatch<
        React.SetStateAction<LiveChatEndConfirmation>
    >;
    greetingMessages: Activity[];
    messageRenderer: MessageRenderer;
}> = ({
    featureFlags,
    modalProperties,
    setModalProperties,
    interactedWithBot,
    setFeedbackDialogIsOpen,
    setInputMode,
    setInteractedWithBot,
    setLiveChatEndConfirmation,
    greetingMessages,
    messageRenderer,
}) => {
    const [isLiveChat, setIsLiveChat] = useState(false);
    const [charLimitOverride, setCharLimitOverride] = useState<number | null>();
    let sendMessageHandler = async (_e: any) => {
        return;
    };
    let sendMessage = (_message: string) => {
        return;
    };
    let sendPostBack = (_message: string) => {
        return;
    };
    let sendEvent = (_name: string, _value: any) => {
        return;
    };
    let messages: string | any[] = [];
    let disableRenderingDuringRehydration = false;
    const { tier, validTranscriptExportOriginList } = useConfig();

    const [activities] = useActivities();
    sendMessage = useSendMessage();
    sendPostBack = useSendPostBack();
    sendEvent = useSendEvent();
    sendMessageHandler = async (e: any) => {
        if (isLiveChat) {
            liveChatService.sendMessage(e);
        } else {
            sendMessage(e);
        }
    };
    const { setSendEvent, inactive, didTimeout, timeoutMessageId } =
        useConversationStatus();
    const lastBotActivity = getLastBotActivity(activities);

    // Register WebChat sendEvent with ConversationStatusProvider for timer-driven events
    useEffect(() => {
        if (sendEvent) {
            setSendEvent(sendEvent);
        }
    }, [sendEvent]);

    if (inactive) {
        const inactiveMessage: Activity = craftCustomActivityMessage(
            quickMessageConfig.inactive
        );
        messages = [inactiveMessage];
    } else if (didTimeout) {
        messages = [...greetingMessages, ...getMessages(activities)];
        const lastMessageBeforeTimeoutIndex = messages.findIndex(
            (message: Activity) => message.id === timeoutMessageId
        );
        if (lastMessageBeforeTimeoutIndex > 0) {
            messages = messages.slice(0, lastMessageBeforeTimeoutIndex + 1);
        }
    } else {
        messages = [...greetingMessages, ...getMessages(activities)];
    }

    useEffect(() => {
        return setupMessageListener(
            setFeedbackDialogIsOpen,
            setLiveChatEndConfirmation,
            interactedWithBot,
            sendEvent,
            tier
        );
    }, [interactedWithBot]);

    useEffect(() => {
        const listener = (ev: MessageEvent) => {
            if (ev?.data !== 'botium-export') {
                return;
            }
            if (validTranscriptExportOriginList.indexOf(ev.origin) >= 0) {
                exportTranscript(activities as Activity[]);
            }
        };
        window.addEventListener('message', listener);
        return () => {
            window.removeEventListener('message', listener);
        };
    });

    const latestActivity = activities?.length
        ? activities[activities.length - 1]
        : null;
    if (latestActivity?.id && isGenesysResume()) {
        const currentWatermark = latestActivity.id.split('|')[1];
        if (
            isRehydratedMessage(featureFlags, currentWatermark) &&
            !isLatestRehydratedMessage(featureFlags, currentWatermark)
        ) {
            disableRenderingDuringRehydration = true;
        }
    }

    useEffect(() => {
        const activity = latestActivity as Activity;
        if (!activity) {
            return;
        }
        if (activity.name == CHARACTER_LIMIT_EVENT_NAME) {
            setCharLimitOverride(activity.value);
        } else if (activity.name == CHARACTER_LIMIT_RESET_EVENT_NAME) {
            setCharLimitOverride(null);
        }
    }, [latestActivity]);

    const multipleUserMessages = hasMultipleUserMessages(messages);
    useEffect(() => {
        if (!interactedWithBot && multipleUserMessages) {
            setInteractedWithBot(true);
        }
    }, [multipleUserMessages, interactedWithBot, setInteractedWithBot]);

    const lastBotMessage = getLastBotMessage(messages);
    const isMenu =
        (lastBotMessage &&
            lastBotMessage.entities &&
            lastBotMessage.entities.some((entity: any) => {
                return entity && entity.name === 'menu';
            })) ||
        false;

    useEffect(() => {
        if (!lastBotMessage?.attachments?.length) {
            return;
        }

        const { contentType, content } = lastBotMessage.attachments[0];

        if (!isLiveChat && contentType === 'application/providence.liveChat') {
            setIsLiveChat(true);
        } else if (
            isLiveChat &&
            !(contentType === 'application/providence.liveChat')
        ) {
            setIsLiveChat(false);
        }

        // Conditionally update MA on the current intent or fulfillment
        if (
            getTenantName() == TENANTS.MESSAGE_ASSIST &&
            [
                'application/providence.buttonlist',
                'application/providence.messageAssistQnaResponse',
            ].includes(contentType)
        ) {
            // Send fulfillment response shown to MA
            setFulfillmentLinkShown(contentType, content);
        }
    }, [lastBotMessage]);

    useEffect(() => {
        const listener = liveChatService.eventsStream.listen((event) => {
            if (event instanceof OnLiveChatClosedEvent) {
                setIsLiveChat(false);
            } else if (event instanceof OnLiveChatClosedInTabEvent) {
                setIsLiveChat(false);
                setInputMode('disabled');
            } else if (event instanceof OnLiveChatOpeningEvent) {
                setIsLiveChat(true);
            }
        });

        return () => {
            listener.dispose();
        };
    }, [liveChatService]);

    // 420 = default rasa character limit
    // https://rasa.com/docs/reference/config/components/llm-command-generators/#customizing-the-maximum-length-of-user-input
    const charLimit = getNumberFromAny(
        featureFlags[GraceFeatureFlags.ChatbotConfigInputCharacterCountLimit],
        420
    );
    const inputCharacterCountLimit = getNumberFromAny(
        charLimitOverride,
        charLimit
    );
    const inputRemainingCharsWarningThreshold = getNumberFromAny(
        featureFlags[GraceFeatureFlags.ChatbotConfigInputCharWarningThreshold],
        50
    );

    const { setInteractionAnchors: setInteractionAnchorsDefault } =
        useConversationStatus();

    // Sync provider watchers and inactive
    useEffect(() => {
        setInteractionAnchorsDefault({
            lastMessage: activities[activities.length - 1] as Activity,
            lastBotActivity,
        });
    }, [activities, lastBotActivity, setInteractionAnchorsDefault]);
    // Inactive is driven by the top-level provider; no prop sync needed here

    if (disableRenderingDuringRehydration) {
        return null;
    }

    return (
        <div className={styles.reactChatContainer}>
            <ReactTranscript
                messages={messages}
                sendMessage={sendMessage}
                sendPostBack={sendPostBack}
                interactedWithBot={interactedWithBot}
                sendEvent={sendEvent}
                featureFlags={featureFlags}
                modalProperties={modalProperties}
                setInputMode={setInputMode}
                setModalProperties={setModalProperties}
                liveChatService={liveChatService}
                messageRenderer={messageRenderer}
            />
            {!inactive && !didTimeout && (
                <InputBox
                    sendMessage={sendMessageHandler}
                    focusCounter={messages.length}
                    isMenu={isMenu}
                    shouldLogAnalyticsEvents={!isLiveChat}
                    inputCharacterCountLimit={inputCharacterCountLimit}
                    inputRemainingCharsWarningThreshold={
                        inputRemainingCharsWarningThreshold
                    }
                />
            )}
        </div>
    );
};

export default ReactChat;
